import Vue from 'vue';

import store from '@core/store';
import ErrorService from '@core/services/ErrorService';

import SubscriptionTypeEnum from '@constants/enums/subscription/type';

import { getCurrentSubscription } from '@packages/rate-plans/utils/currentSubscription';

import { getQueryParam } from '@utils/browser';

import { Investor } from '@/models';

export function isRootPlatformAdviser(adviser = null) {
    if (!adviser) {
        return false;
    }

    return adviser.root === true || adviser.is_root === true;
}

// The firm selector is GI-only; adviser and investor sessions are scoped to their own firm.
export function getBillingPanelAdviserOptions(organisation = null, auth = null) {
    if (auth?.is_investor || !auth?.is_gi || !Array.isArray(organisation?.adviser_accounts)) {
        return [];
    }

    return organisation.adviser_accounts
        .map(account => {
            const adviserId = account?.adviser?.id || account?.adviser_id;

            if (!adviserId) {
                return null;
            }

            return {
                id: `${adviserId}`,
                name: account?.adviser?.name || `Adviser ${adviserId}`,
                adviser: account?.adviser || null
            };
        })
        .filter(Boolean)
        .filter((item, index, items) => items.findIndex(candidate => candidate.id === item.id) === index)
        .filter(option => option.adviser && !isRootPlatformAdviser(option.adviser));
}

export function resolveBillingPanelScopedAdviserId(organisation = null, auth = null) {
    const options = getBillingPanelAdviserOptions(organisation, auth);

    if (options.length === 1) {
        return options[0].id;
    }

    if (options.length > 1) {
        return null;
    }

    // No non-root intermediary on the client: fall back to the viewer's own relationship
    // so the Intermediary Fees section still loads.
    return auth?.adviser_id ? `${auth.adviser_id}` : null;
}

/**
 * @param {object} [params]
 * @param {string|null} [params.scopedAdviserId] an already-resolved scope; when given it is the
 *   single-option fallback, so `organisation`/`auth` are not consulted.
 */
export function resolveBillingPanelAdviserId({
    organisation = null,
    auth = null,
    selectedAdviserId = null,
    adviserOptions = null,
    scopedAdviserId
} = {}) {
    const options = adviserOptions || getBillingPanelAdviserOptions(organisation, auth);

    if (options.length > 1) {
        const selectedId = selectedAdviserId ? `${selectedAdviserId}` : null;
        const selectedOption = options.find(option => option.id === selectedId);

        return selectedOption?.id || options[0]?.id || null;
    }

    if (scopedAdviserId !== undefined) {
        return scopedAdviserId ? `${scopedAdviserId}` : null;
    }

    return resolveBillingPanelScopedAdviserId(organisation, auth);
}

export const INVESTOR_BILLING_ADVISER_QUERY_KEY = 'investor_billing_adviser_id';

export function getSelectedBillingAdviserId() {
    const queryValue = getQueryParam(INVESTOR_BILLING_ADVISER_QUERY_KEY);

    if (queryValue) {
        return `${queryValue}`;
    }

    const querySelector = store.getters['app/query_selector'];
    const selectedAdviserId =
        typeof querySelector === 'function' ? querySelector(INVESTOR_BILLING_ADVISER_QUERY_KEY) : null;

    return selectedAdviserId ? `${selectedAdviserId}` : null;
}

export function resolveBillingContextAdviserAccount(organisation, auth, selectedAdviserId = null) {
    const adviserOptions = getBillingPanelAdviserOptions(organisation, auth);
    const adviserId = resolveBillingPanelAdviserId({
        organisation,
        auth,
        selectedAdviserId,
        adviserOptions
    });

    if (!adviserId || !Array.isArray(organisation?.adviser_accounts)) {
        return null;
    }

    return organisation.adviser_accounts.find(aa => `${aa?.adviser?.id || aa?.adviser_id}` === `${adviserId}`) || null;
}

export function evaluatePlanIdFromSubscriptionData(data = {}, subscriptionType = SubscriptionTypeEnum.ADVISER) {
    const subscriptions = Array.isArray(data.subscriptions) ? data.subscriptions : [];
    const subscription = getCurrentSubscription(subscriptions, {
        subscriptionType
    });

    return subscription?.rate_plan_id || null;
}

const RELATIONSHIP_FEE_FLAGS = ['adviser_fees_agreed_externally', 'facilitate_adviser_fees'];

/**
 * Copies the relationship's fee flags onto the matching adviser account so model validation can read
 * them; a missing relationship clears both to null.
 * @returns {boolean} whether anything changed.
 */
export function hydrateOrganisationAdviserRelationship(organisation, adviserId, relationship = null) {
    if (!organisation || !adviserId || !Array.isArray(organisation.adviser_accounts)) {
        return false;
    }

    const normalisedAdviserId = `${adviserId}`;
    const account = organisation.adviser_accounts.find(
        aa => `${aa?.adviser?.id || aa?.adviser_id}` === normalisedAdviserId
    );

    if (!account) {
        return false;
    }

    let updated = false;

    for (const field of RELATIONSHIP_FEE_FLAGS) {
        const value = typeof relationship?.[field] === 'boolean' ? relationship[field] : null;

        if (account[field] !== value) {
            Vue.set(account, field, value);
            updated = true;
        }
    }

    return updated;
}

/**
 * @returns {Promise<{ adviserId: string, relationship: object|null }|null>} null for a non-investor
 *   organisation, no resolvable adviser, or a failure other than 404.
 */
export async function fetchInvestorAdviserRelationshipContext({
    organisation = null,
    auth = null,
    selectedAdviserId = null
} = {}) {
    if (!organisation?.is_investor || !organisation?.id) {
        return null;
    }

    const adviserOptions = getBillingPanelAdviserOptions(organisation, auth);
    const adviserId = resolveBillingPanelAdviserId({
        organisation,
        auth,
        selectedAdviserId: selectedAdviserId ?? getSelectedBillingAdviserId(),
        adviserOptions
    });

    if (!adviserId) {
        return null;
    }

    try {
        // persistent: this also runs on Save, which may follow a PATCH within the cache window, and
        // the cached GET would still carry the value from before it.
        const response = await Investor.api.getAdviserRelationship(
            organisation.id,
            adviserId,
            {},
            { persistent: true }
        );

        return { adviserId, relationship: response?.data || null };
    } catch (error) {
        if (error?.code === 404) {
            return { adviserId, relationship: null };
        }

        ErrorService.log(error);

        return null;
    }
}
